import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getParagraph14BoldTypography } from "../../../Typography";
import AuctionBidPanel from "./AuctionBidPanel";
import AuctionTimer from "../AuctionTimer";
import { IBid, IListing } from "../../../commonTypes";
import Button from "../../Buttons/Button";
import { useTimer } from "../../../hooks/useTimer";
import ShareBidButton from "../../ShareBidButton/ShareBidButton";
import { getVerticalGap } from "../../../styles";

const Root = styled.div``;

const Container = styled.div`
  display: flex;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.backgroundColor};
  backdrop-filter: ${({ theme }) => theme.filterBlur};
  padding: 23px 20px 20px 20px;
  flex-direction: column;

  hr {
    border-color: ${({ theme }) => theme.borderColor};
    border-style: solid;
    border-bottom-width: 0;
  }
`;

const BidDetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  padding-bottom: 36px;
`;

const AuctionDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding-top: 20px;
  padding-bottom: 32px;
`;

const Buttons = styled.div`
  ${getVerticalGap("10px")};
`;

const PlaceBidCta = styled.p`
  ${getParagraph14BoldTypography};
  margin: auto;
  color: ${({ theme }) => theme.primaryColor};
`;

type Props = {
  bid?: IBid;
  listing: IListing;
  price: string;
  priceUsd: string;
  currency: string;
  endTime: string;
  onClick: () => void;
};

const AuctionBidDetails = ({
  bid,
  listing,
  price,
  priceUsd,
  currency,
  endTime,
  onClick,
}: Props) => {
  const { t } = useTranslation();

  const { isComplete, label, onTimerCompleted } = useTimer({
    inProgressLabel: t("featured-track-aution-ends"),
    endedLabel: t("auction-ended"),
  });

  return (
    <Root>
      <Container>
        <BidDetailsContainer>
          <AuctionBidPanel
            bid={bid}
            price={price}
            priceUsd={priceUsd}
            currency={currency}
          />
        </BidDetailsContainer>
        <hr />
        <AuctionDetails>
          <AuctionTimer
            title={t(label)}
            opacity={"0.7"}
            endTime={endTime}
            onComplete={onTimerCompleted}
            showWhenEnded={false}
          />
        </AuctionDetails>
        <Buttons>
          <ShareBidButton music={listing.music} listingId={listing.id} />
          <Button
            label={<PlaceBidCta>{t("featured-track-place-a-bid")}</PlaceBidCta>}
            fullWidth
            height="48px"
            onClick={onClick}
            disabled={isComplete}
          />
        </Buttons>
      </Container>
    </Root>
  );
};

export default AuctionBidDetails;
